import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Mail, Play, Archive, Clock, Send } from 'lucide-react';
import { Button, Card, EmptyState, Skeleton } from '@solid/ui';
import { cn } from '@/lib/utils';
import {
  useSequences,
  useCreateSequence,
} from '@/hooks/useSequences';
import type { SequenceStatus } from '@/types';

const statusTabs: { label: string; value: string | undefined; icon: any }[] = [
  { label: 'Active', value: 'ACTIVE,PAUSED', icon: Play },
  { label: 'Draft', value: 'DRAFT', icon: Clock },
  { label: 'Archived', value: 'ARCHIVED', icon: Archive },
];

const statusColors: Record<SequenceStatus, string> = {
  DRAFT: 'bg-grey-100 text-grey-700',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-700',
  ACTIVE: 'bg-green-100 text-green-700',
  PAUSED: 'bg-blue-100 text-blue-700',
  ARCHIVED: 'bg-grey-100 text-grey-500',
};

const statusLabels: Record<SequenceStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  ARCHIVED: 'Archived',
};

export default function SequenceSettings() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string | undefined>('ACTIVE,PAUSED');
  const { data: sequences, isLoading } = useSequences({ status: statusFilter });
  const createMutation = useCreateSequence();

  const handleCreate = async () => {
    const sequence = await createMutation.mutateAsync({
      name: 'New Sequence',
    });
    navigate(`/admin/sequences/${sequence.id}`);
  };

  return (
    <div className="page-enter space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-heading-1 text-secondary flex items-center gap-3">
            <Send className="w-7 h-7 text-grey-400" />
            Sequences
          </h1>
          <p className="text-body-sm text-grey-500 mt-1">
            Manage automated outbound email sequences.
          </p>
        </div>
        <Button onClick={handleCreate} loading={createMutation.isPending}>
          <Plus className="w-4 h-4" />
          New Sequence
        </Button>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1 border-b border-grey-200 pb-px">
        {statusTabs.map((tab) => (
          <button
            key={tab.label}
            onClick={() => setStatusFilter(tab.value)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2',
              statusFilter === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-grey-500 hover:text-secondary',
            )}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : !sequences?.length ? (
        <Card padding="md">
          <EmptyState
            icon={<Mail />}
            title="No sequences found"
            action={
              <Button size="sm" onClick={handleCreate}>
                <Plus className="w-4 h-4" />
                Create your first sequence
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {sequences.map((seq) => (
            <Card
              key={seq.id}
              padding="md"
              className="cursor-pointer hover:border-primary/30 transition-colors"
              onClick={() => navigate(`/admin/sequences/${seq.id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center">
                    <Send className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-secondary">{seq.name}</h3>
                    <p className="text-xs text-grey-500">
                      {seq.steps.length} step{seq.steps.length !== 1 ? 's' : ''}
                      {seq._count?.enrollments ? ` · ${seq._count.enrollments} enrolled` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      'px-2.5 py-1 rounded-full text-xs font-medium',
                      statusColors[seq.status],
                    )}
                  >
                    {statusLabels[seq.status]}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
